/**
 * f14.2: the reconciliation queue's reads and its one write (design D8).
 *
 * All keys sit under `['cycle', id, 'reconciliation']`, so a decision -- or a
 * `409` telling us the row moved -- refetches the list and the summary in one
 * invalidation. An ACCEPT also changes the cycle's figures (the unit is
 * recalculated), so the review and forecast keys are invalidated too.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, api, type ReconciliationQuery } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';

export interface DecisionInput {
  varianceId: string;
  decision: 'ACCEPT' | 'REJECT';
  comment: string | null;
}

export function useVarianceList(cycleId: string, query: ReconciliationQuery) {
  return useQuery({
    queryKey: queryKeys.cycle.reconciliationList(cycleId, query),
    queryFn: () => api.listReconciliationVariances(cycleId, query),
    enabled: Boolean(cycleId),
    retry: false,
    placeholderData: (previous) => previous,
  });
}

export function useReconciliationSummary(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.reconciliationSummary(cycleId),
    queryFn: () => api.getReconciliationSummary(cycleId),
    enabled: Boolean(cycleId),
    retry: false,
  });
}

export function useDecideVariance(cycleId: string) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.cycle.reconciliation(cycleId) });
  return useMutation({
    mutationFn: ({ varianceId, decision, comment }: DecisionInput) =>
      api.decideReconciliationVariance(varianceId, { decision, comment }),
    onSuccess: (decided) => {
      void refresh();
      if (decided.status.code === 'ACCEPTED') {
        void queryClient.invalidateQueries({ queryKey: ['review', cycleId] });
      }
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) void refresh();
    },
  });
}

/** The server's own message for the toast, whatever the refusal. */
export function decisionFailure(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : 'The decision could not be recorded.';
}
